import { isNil } from 'lodash';
import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Ellipsis, Icon } from 'components/elements';
import { getNextSortDirection } from 'lib/sort';
import { StyledTableHeaderColumn, TableHeaderColumnResizer } from './TableHeaderColumn.styled';
import { TableHeaderColumnProps } from './types';

export const TableHeaderColumn = <TEntity extends unknown>(
  props: TableHeaderColumnProps<TEntity>,
) => {
  const { column, onResize, onSort } = props;

  // Reference to the main header column element
  const ref = useRef<HTMLDivElement>(null);

  // Reference to the column resizer element
  const resizerRef = useRef<HTMLDivElement>(null);

  // Generate column style by applying specific width or using 'flex: 1' for unknown widths
  const style = useMemo<CSSProperties>(
    () => ({
      flex: isNil(column.width) ? 1 : undefined,
      width: isNil(column.width) ? undefined : `${column.width}px`,
    }),
    [column.width],
  );

  // Store starting element width and mouse position when resize starts
  const startX = useRef<number>(0);
  const startWidth = useRef<number>(0);

  /**
   * Handle changing column sort direction
   */
  const handleMouseDown = useCallback(() => {
    // Ensure column is sortable
    if (!column.sortKey || !onSort) {
      return;
    }

    const next = getNextSortDirection(column.sortDir);
    const key = next ? column.sortKey.toString() : undefined;
    onSort(key, next);
  }, [column.sortDir, column.sortKey, onSort]);

  /**
   * Handle changes to the column width
   *
   * @param {number} width Updated column width
   */
  const handleResize = useCallback(
    (width: number) => {
      // Round the width down to avoid updates for decimal width changes
      width = Math.floor(width);

      // Notify the main table component to ensure the calculated width is persisted
      onResize(column.id.toString(), width);
    },
    [column.id, onResize],
  );

  /**
   * Handle mouse move while dragging the resizer (i.e. resizing the column)
   *
   * @param {MouseEvent} event Mouse event object
   */
  const handleDocumentMouseMove = useCallback(
    (event: MouseEvent) => {
      // Calculate the difference between the current mouse position and start drag position
      const diff = event.clientX - startX.current;

      // Calculate target column width ensuring it stays within the minimal boundaries
      let targetWidth = Math.max(column.minWidth ?? 0, startWidth.current + diff);

      // Limit maximum column width if specified in the column configuration
      if (column.maxWidth) {
        targetWidth = Math.min(targetWidth, column.maxWidth);
      }

      handleResize(targetWidth);
    },
    [column.maxWidth, column.minWidth, handleResize],
  );

  /**
   * Handle releasing the mouse after dragging the resizer
   */
  const handleDocumentMouseUp = useCallback(
    () => document.removeEventListener('mousemove', handleDocumentMouseMove),
    [handleDocumentMouseMove],
  );

  /**
   * Handle grabbing the resizer
   *
   * @param {ReactMouseEvent} event Mouse event object
   */
  const handleResizerMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!ref.current) {
        return;
      }

      // Prevent header component's mouse down event from triggering
      event.stopPropagation();

      // Persist initial horizontal mouse position
      startX.current = event.clientX;

      // Persist initial column width to use when calculating X axis offset changes
      startWidth.current = ref.current.getBoundingClientRect().width;

      // Listen for mouse being moved while the left mouse button is pressed down
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp, {
        once: true,
      });
    },
    [handleDocumentMouseMove, handleDocumentMouseUp],
  );

  /**
   * Render column header resizer when applicable
   */
  const renderResizer = () => {
    // Ensure column is resizable before proceeding
    if (!column.resizable) {
      return null;
      }

      const idfromSymbol = String(column.id)

    return (
      <TableHeaderColumnResizer
        draggable={false}
            key={`resizer:${idfromSymbol}`}
        ref={resizerRef}
        role="separator"
        onMouseDown={handleResizerMouseDown}
      />
    );
  };

  // Calculate column element width on mount to determine the size of flex columns
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    // Retrieve the current element width
    let { width } = ref.current.getBoundingClientRect();

    // Ensure column meets the minimum allowed width
    if (column.minWidth) {
      width = Math.max(column.minWidth, width);
    }

    // Ensure column meets the maximum allowed width
    if (column.maxWidth) {
      width = Math.min(width, column.maxWidth);
    }

    handleResize(width);
  }, [column.maxWidth, column.minWidth, handleResize]);

  return (
    <StyledTableHeaderColumn
      $align={column.align}
      $sort={column.sortDir}
      $sortable={!!column.sortKey}
      key={column.id.toString()}
      ref={ref}
      role="columnheader"
      style={style}
      onMouseDown={handleMouseDown}
    >
      <Ellipsis>{column.header}</Ellipsis>
      {column.sortDir && <Icon name="caret up" />}
      {renderResizer()}
    </StyledTableHeaderColumn>
  );
};
